import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import Spinner from '../Atoms/Spinner';

const EVENT_ID = '67590da5499bcc995c210b40';

// ─── Styled Components ───────────────────────────────────────
const PageContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LogoImg = styled.img`
    display: block;
    margin: 0 auto 20px;
    max-width: 180px;
`;

const PageTitle = styled.h1`
    text-align: center;
    font-size: 1.6rem;
    color: #1a1a2e;
    margin-bottom: 8px;
    font-weight: 700;
`;

const PageSubtitle = styled.p`
    text-align: center;
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 32px;
`;

const StatsBar = styled.div`
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 32px;
    flex-wrap: wrap;
`;

const StatBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    background: ${p => p.color || '#f5f5f5'};
    color: ${p => p.textColor || '#333'};
`;

const PieceCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
    transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.08); }
`;

const PieceHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    gap: 12px;
    flex-wrap: wrap;
`;

const PieceName = styled.h3`
    font-size: 1.1rem;
    color: #1a1a2e;
    margin: 0;
    font-weight: 700;
`;

const DesignerName = styled.span`
    font-size: 0.95rem;
    color: #555;
    font-weight: 400;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: ${p => p.submitted ? '#d4edda' : '#fff3cd'};
    color: ${p => p.submitted ? '#155724' : '#856404'};
    white-space: nowrap;
`;

const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px 16px;
    margin-bottom: 14px;
`;

const Field = styled.div`
    font-size: 13px;
    color: #333;
    label {
        font-weight: 600;
        color: #888;
        display: block;
        font-size: 11px;
        text-transform: uppercase;
        margin-bottom: 2px;
    }
`;

const Description = styled.div`
    font-size: 13px;
    color: #555;
    line-height: 1.5;
    margin-bottom: 14px;
    padding: 10px 14px;
    background: #f8f9fa;
    border-radius: 6px;
    white-space: pre-wrap;
`;

const ContactRow = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #555;
    padding-top: 10px;
    border-top: 1px solid #f0f0f0;
    margin-top: 10px;
`;

const PhotosGrid = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const PhotoCard = styled.div`
    position: relative;
    width: 140px;
    flex-shrink: 0;
`;

const PhotoImg = styled.img`
    width: 140px;
    height: 140px;
    object-fit: cover;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s;
    display: block;
    &:hover { transform: scale(1.03); }
`;

const PhotoMeta = styled.div`
    font-size: 11px;
    color: #888;
    margin-top: 4px;
    line-height: 1.3;
`;

const CreditLabel = styled.div`
    font-size: 12px;
    color: #555;
    font-weight: 600;
    font-style: italic;
`;

const DownloadBtn = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    background: #f0f4ff;
    color: rgb(24, 51, 189);
    text-decoration: none;
    margin-top: 4px;
    &:hover { background: #d0d8ff; }
`;

const LightboxOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.85);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    img {
        max-width: 90vw;
        max-height: 90vh;
        object-fit: contain;
        border-radius: 8px;
    }
`;

// ─── Component ───────────────────────────────────────────────
const FD100ExpoFinalePublic = () => {
    const [searchParams] = useSearchParams();
    const singlePieceId = searchParams.get('piece');
    const [loading, setLoading] = useState(true);
    const [pieces, setPieces] = useState([]);
    const [lightboxUrl, setLightboxUrl] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            let query = supabase
                .from('fd100_expo_finale_pieces')
                .select('*')
                .eq('event_id', EVENT_ID);

            if (singlePieceId) {
                query = query.eq('id', singlePieceId);
            }

            const { data, error } = await query.order('nom_piece');

            if (error) {
                console.error('Error loading pieces:', error);
            } else {
                const HIDDEN_PIECES = ['totémique', 'totemique'];
                const visible = (data || []).filter(p => !HIDDEN_PIECES.includes((p.nom_piece || '').toLowerCase().trim()));
                setPieces(visible);
            }
            setLoading(false);
        };
        fetchData();
    }, [singlePieceId]);

    if (loading) {
        return (
            <PageContainer>
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spinner />
                </div>
            </PageContainer>
        );
    }

    const submitted = pieces.filter(p => p.submitted_at);
    const pending = pieces.filter(p => !p.submitted_at);

    const normalize = s => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[-–—'']/g, ' ').replace(/\s+/g, ' ').trim();

    const handleDownload = async (url, filename) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename || url.split('/').pop() || 'photo.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (err) {
            window.open(url, '_blank');
        }
    };

    return (
        <PageContainer>
            <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
            <PageTitle>
                {singlePieceId && pieces.length === 1
                    ? `${pieces[0].nom_piece || 'Pièce'}`
                    : 'Exposition « Hospitalité lyrique »'
                }
            </PageTitle>
            <PageSubtitle>
                {singlePieceId && pieces.length === 1
                    ? `${pieces[0].designer_names || ''} ${pieces[0].fabricant ? `— ${pieces[0].fabricant}` : ''}`
                    : 'Suivi des pièces — Données en temps réel'
                }
            </PageSubtitle>

            {!singlePieceId && <StatsBar>
                <StatBadge color="#e8f4f8" textColor="#1a5276">
                    {pieces.length} pièces
                </StatBadge>
                <StatBadge color="#d4edda" textColor="#155724">
                    {submitted.length} soumis
                </StatBadge>
                <StatBadge color="#fff3cd" textColor="#856404">
                    {pending.length} en attente
                </StatBadge>
            </StatsBar>}

            {lightboxUrl && (
                <LightboxOverlay onClick={() => setLightboxUrl(null)}>
                    <img src={lightboxUrl} alt="" />
                </LightboxOverlay>
            )}

            {pieces.map(piece => (
                <PieceCard key={piece.id}>
                    <PieceHeader>
                        <div>
                            <PieceName>
                                {piece.nom_piece || '—'}
                                {piece.designer_names && (
                                    <DesignerName> — {piece.designer_names}</DesignerName>
                                )}
                            </PieceName>
                            {piece.fabricant && (
                                <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                                    {piece.fabricant}
                                </div>
                            )}
                        </div>
                        <StatusBadge submitted={!!piece.submitted_at}>
                            {piece.submitted_at ? 'Soumis' : 'En attente'}
                        </StatusBadge>
                    </PieceHeader>

                    <FieldsGrid>
                        {piece.typologie && (
                            <Field><label>Typologie</label>{piece.typologie}</Field>
                        )}
                        {piece.dimensions && (
                            <Field><label>Dimensions</label>{piece.dimensions}</Field>
                        )}
                        {piece.materiaux && (
                            <Field><label>Matériaux</label>{piece.materiaux}</Field>
                        )}
                        {piece.poids && (
                            <Field><label>Poids</label>{piece.poids}</Field>
                        )}
                        {piece.valeur_assurance && (
                            <Field><label>Valeur d'assurance</label>{piece.valeur_assurance}</Field>
                        )}
                    </FieldsGrid>

                    {piece.description && (
                        <Description>{piece.description}</Description>
                    )}

                    {(piece.photos || []).length > 0 && (
                        <PhotosGrid>
                            {piece.photos.map((photo, i) => (
                                <PhotoCard key={i}>
                                    <PhotoImg
                                        src={photo.url}
                                        alt={`${piece.nom_piece} - ${i + 1}`}
                                        onClick={() => setLightboxUrl(photo.url)}
                                    />
                                    <PhotoMeta>
                                        {photo.credit && <CreditLabel>Credit: {photo.credit}</CreditLabel>}
                                        {photo.category && (
                                            <div style={{ color: '#aaa' }}>
                                                {photo.category === 'packshot' ? 'Packshot' : 'Contexte'}
                                            </div>
                                        )}
                                    </PhotoMeta>
                                    <DownloadBtn
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault();
                                            handleDownload(photo.url, `${normalize(piece.nom_piece)}_${i + 1}.jpg`);
                                        }}
                                    >
                                        Télécharger
                                    </DownloadBtn>
                                </PhotoCard>
                            ))}
                        </PhotosGrid>
                    )}

                    {(piece.contact_prenom || piece.contact_nom || piece.contact_email || piece.contact_telephone) && (
                        <ContactRow>
                            {(piece.contact_prenom || piece.contact_nom) && (
                                <span><strong>{piece.contact_prenom} {piece.contact_nom}</strong></span>
                            )}
                            {piece.contact_email && <span>{piece.contact_email}</span>}
                            {piece.contact_telephone && <span>{piece.contact_telephone}</span>}
                        </ContactRow>
                    )}
                </PieceCard>
            ))}
        </PageContainer>
    );
};

export default FD100ExpoFinalePublic;
